// FleetStatus — Fleet & Apparatus management dashboard.
// Sections: KPI row, Fleet Overview table, Replacement Schedule, Compliance Alerts,
// IRONS cross-reference bar chart.

import { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import { useFleet, useFleetReplacement, useIrons } from "../hooks/useOps";
import type { FleetRow, FleetReplacementRow, IronRow } from "../hooks/useOps";
import { useAggregates } from "../hooks/useDashboard";
import KPICard from "../components/dashboard/KPICard";
import ChartCard from "../components/dashboard/ChartCard";
import AlertList from "../components/dashboard/AlertList";
import type { AlertItem } from "../components/dashboard/AlertList";
import StatusBadge from "../components/dashboard/StatusBadge";
import { TruncationNoticeGroup } from "../components/shared/TruncationNotice";
import FleetTable from "../components/dashboard/FleetTable";
import type { FleetRow as FleetTableRow } from "../components/dashboard/FleetTable";
import DateRangeToggle, {
  useSinceDate,
  isAfterSince,
} from "../components/dashboard/DateRangeToggle";
import type { DateRange } from "../components/dashboard/DateRangeToggle";
import { useTheme } from "../hooks/useTheme";
import { getChartTheme } from "../lib/chart-theme";

// ── Constants ─────────────────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear();
const DEFAULT_MAX_AGE = 20;
const DCVFD_ACCENT = "#2a7a6c";
const BAR_DANGER = "#dc2626";
const BAR_WARNING = "#f59e0b";
const _BAR_NORMAL = "#2a7a6c";

// ── Helpers ───────────────────────────────────────────────────────────────────

function asNumber(v: unknown, fallback = 0): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function asString(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v : fallback;
}

function asBoolean(v: unknown): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    const lower = v.toLowerCase().trim();
    return lower === "true" || lower === "1" || lower === "yes";
  }
  return false;
}

function computeYearsInService(year: number | null | undefined): number {
  if (!year || year <= 0) return 0;
  return Math.max(0, CURRENT_YEAR - year);
}

function isOutOfService(status: string | null | undefined): boolean {
  if (!status) return false;
  const norm = status.toLowerCase().replace(/[-_\s]/g, "");
  return norm === "outofservice";
}

// ── Data transformations ──────────────────────────────────────────────────────

/**
 * Map a FleetRow (from API) to the FleetTableRow shape expected by <FleetTable>.
 * The replacement map lets us supply maxAge per unit.
 */
function toFleetTableRow(
  row: FleetRow,
  replacementByUnit: Map<string, FleetReplacementRow>,
): FleetTableRow {
  const unit = asString(row.unit ?? row.unit_number);
  const vehicleType = asString(row.vehicle_type ?? row.apparatus_type);
  const assignment = asString(row.assignment ?? row.station);
  const year = asNumber(row.year);
  const status = asString(row.status, "Unknown");
  const yearsInService = computeYearsInService(year);

  // Try to get maxAge from replacement schedule
  const replRow = replacementByUnit.get(unit);
  const maxAge = replRow
    ? asNumber(
        replRow.max_age ?? (replRow as Record<string, unknown>).max_age,
        DEFAULT_MAX_AGE,
      )
    : DEFAULT_MAX_AGE;

  return {
    unit,
    vehicleType,
    assignment,
    year,
    status,
    yearsInService,
    maxAge,
  };
}

interface ReplacementDisplayRow {
  unit: string;
  vehicleType: string;
  yearsInService: number;
  maxAge: number;
  /** false when the source row has no recorded max_age — maxAge is then a UI default, not data. */
  maxAgeKnown: boolean;
  ageDifference: number;
  beyondRepair: boolean;
}

function toReplacementDisplayRow(
  row: FleetReplacementRow,
): ReplacementDisplayRow {
  const unit = asString(
    row.unit_number ?? (row as Record<string, unknown>).name,
  );
  const vehicleType = asString(
    (row as Record<string, unknown>).vehicle_type_from_apparatus ??
      row.apparatus_type ??
      (row as Record<string, unknown>).apparatus,
  );
  const yearsInService = asNumber(
    (row as Record<string, unknown>).years_in_service ?? row.current_year,
  );
  // Distinguish a recorded max age from a missing one. ~71% of replacement
  // rows have a NULL max_age; presenting the DEFAULT_MAX_AGE as if it were a
  // recorded service life (and painting rows green/red off it) is fabricated.
  const rawMax = (row as Record<string, unknown>).max_age;
  const maxAgeKnown =
    typeof rawMax === "number" ||
    (typeof rawMax === "string" &&
      rawMax.trim() !== "" &&
      !isNaN(Number(rawMax)));
  const maxAge = maxAgeKnown
    ? asNumber(rawMax, DEFAULT_MAX_AGE)
    : DEFAULT_MAX_AGE;
  const ageDifference = asNumber(
    (row as Record<string, unknown>).age_difference,
    yearsInService - maxAge,
  );
  const beyondRepair = asBoolean(
    (row as Record<string, unknown>).beyond_repair,
  );

  return {
    unit,
    vehicleType,
    yearsInService,
    maxAge,
    maxAgeKnown,
    ageDifference,
    beyondRepair,
  };
}

/** Group IRONS rows by apparatus unit, returning a sorted array of {unit, count}. */
function groupIronsByUnit(
  irons: IronRow[],
): { unit: string; count: number; color: string }[] {
  const counts = new Map<string, number>();
  for (const iron of irons) {
    const unit = asString(iron.apparatus ?? iron.station);
    if (!unit) continue;
    counts.set(unit, (counts.get(unit) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15)
    .map(([unit, count], idx) => ({
      unit,
      count,
      color: idx === 0 ? BAR_DANGER : idx <= 2 ? BAR_WARNING : DCVFD_ACCENT,
    }));
}

// ── Row color helpers ─────────────────────────────────────────────────────────

function replacementRowClass(row: ReplacementDisplayRow): string {
  // beyondRepair is a recorded flag and always actionable.
  if (row.beyondRepair) {
    return "border-l-4 border-l-red-500 bg-red-500/5";
  }
  // Without a recorded max age we cannot judge service life — stay neutral
  // rather than painting an unknown-life rig green/red off a UI default.
  if (!row.maxAgeKnown) {
    return "";
  }
  if (row.yearsInService > row.maxAge) {
    return "border-l-4 border-l-red-500 bg-red-500/5";
  }
  if (row.yearsInService > row.maxAge * 0.8) {
    return "border-l-4 border-l-amber-400 bg-amber-400/5";
  }
  return "";
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface ReplacementTableProps {
  rows: ReplacementDisplayRow[];
  isLoading: boolean;
}

function ReplacementTable({ rows, isLoading }: ReplacementTableProps) {
  if (isLoading) {
    return (
      <div className="glass rounded-2xl overflow-hidden">
        <div className="px-5 pt-5 pb-3 border-b border-theme-subtle">
          <div className="skeleton h-4 w-48 rounded-md" />
        </div>
        <div className="p-4 space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="skeleton h-10 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="glass rounded-2xl overflow-hidden">
      <div className="px-5 pt-5 pb-3 border-b border-theme-subtle flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-theme-primary leading-tight">
          Replacement Schedule
        </h2>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-theme-overlay text-theme-muted">
          {rows.length} unit{rows.length !== 1 ? "s" : ""}
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="py-10 text-center">
          <p className="text-sm text-theme-muted">
            No replacement schedule data available
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-theme-subtle">
                {[
                  "Unit",
                  "Type",
                  "Years in Service",
                  "Max Age",
                  "Difference",
                  "Beyond Repair",
                ].map((col) => (
                  <th
                    key={col}
                    className="px-4 py-2.5 text-left text-xs font-semibold text-theme-muted uppercase tracking-wider whitespace-nowrap"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.unit}
                  className={`transition-colors hover:bg-theme-overlay/50 ${replacementRowClass(row)}`}
                >
                  <td className="px-4 py-2.5 font-semibold text-theme-primary whitespace-nowrap">
                    {row.unit}
                  </td>
                  <td className="px-4 py-2.5 text-theme-secondary whitespace-nowrap">
                    {row.vehicleType || "—"}
                  </td>
                  <td className="px-4 py-2.5 tabular-nums text-theme-secondary">
                    {row.yearsInService}
                  </td>
                  <td className="px-4 py-2.5 tabular-nums text-theme-secondary">
                    {row.maxAgeKnown ? row.maxAge : "—"}
                  </td>
                  <td className="px-4 py-2.5 tabular-nums">
                    <span
                      className={`font-semibold ${
                        row.ageDifference > 0
                          ? "text-status-red"
                          : row.ageDifference > -(row.maxAge * 0.2)
                            ? "text-status-amber"
                            : "text-status-green"
                      }`}
                    >
                      {row.ageDifference > 0
                        ? `+${row.ageDifference}`
                        : row.ageDifference}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    {row.beyondRepair ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                        Yes
                      </span>
                    ) : (
                      <span className="text-xs text-theme-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div className="px-5 py-3 border-t border-theme-subtle flex items-center gap-4">
        <span className="text-xs text-theme-muted font-medium">Status:</span>
        {[
          { color: "bg-status-red", label: "Overdue / Beyond repair" },
          { color: "bg-status-amber", label: ">80% of max age" },
          { color: "bg-status-green", label: "Within service life" },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
            <span className="text-xs text-theme-muted">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function FleetStatus() {
  const [range, setRange] = useState<DateRange>("all");
  const sinceDate = useSinceDate(range);

  const { resolvedTheme } = useTheme();
  const chart = getChartTheme(resolvedTheme === "dark");

  const fleetResult = useFleet();
  const replacementResult = useFleetReplacement();
  const ironsResult = useIrons();
  const aggregatesResult = useAggregates();

  const fleetRows: FleetRow[] = fleetResult.data?.data ?? [];
  const replacementRows: FleetReplacementRow[] =
    replacementResult.data?.data ?? [];
  const allIronRows: IronRow[] = ironsResult.data?.data ?? [];
  const aggregates = aggregatesResult.data?.aggregates ?? {};

  // ── Filter IRONS by date range for the cross-reference chart ─────────────
  const ironRows = useMemo<IronRow[]>(() => {
    if (!sinceDate) return allIronRows;
    return allIronRows.filter((r) =>
      isAfterSince(asString((r.created_on as unknown) ?? ""), sinceDate),
    );
  }, [allIronRows, sinceDate]);

  const isLoading =
    fleetResult.isLoading ||
    replacementResult.isLoading ||
    ironsResult.isLoading ||
    aggregatesResult.isLoading;

  // ── KPI values ──────────────────────────────────────────────────────────────

  const kpis = useMemo(() => {
    // Use aggregates if available, else compute from fleet rows
    const inService = asNumber(
      aggregates.fleet_in_service_count,
      fleetRows.filter((r) => !isOutOfService(asString(r.status))).length,
    );
    const total = asNumber(aggregates.fleet_total_count, fleetRows.length);
    const outOfService = total - inService;

    const years = fleetRows
      .map((r) => computeYearsInService(asNumber(r.year) || undefined))
      .filter((y) => y > 0);
    const avgAge =
      years.length > 0
        ? Math.round((years.reduce((a, b) => a + b, 0) / years.length) * 10) /
          10
        : 0;

    return { inService, outOfService, total, avgAge };
  }, [fleetRows, aggregates]);

  // ── Replacement map (unit → row) for FleetTable maxAge lookup ──────────────

  const replacementByUnit = useMemo(() => {
    const map = new Map<string, FleetReplacementRow>();
    for (const row of replacementRows) {
      const unit = asString(
        row.unit_number ?? (row as Record<string, unknown>).name,
      );
      if (unit) map.set(unit, row);
    }
    return map;
  }, [replacementRows]);

  // ── Fleet table rows ────────────────────────────────────────────────────────

  const fleetTableRows: FleetTableRow[] = useMemo(
    () => fleetRows.map((r) => toFleetTableRow(r, replacementByUnit)),
    [fleetRows, replacementByUnit],
  );

  // ── Replacement schedule (sorted by ageDifference asc = most overdue first) ─

  const replacementDisplayRows: ReplacementDisplayRow[] = useMemo(() => {
    return replacementRows
      .map(toReplacementDisplayRow)
      .sort((a, b) => b.ageDifference - a.ageDifference);
  }, [replacementRows]);

  // ── Compliance alerts ───────────────────────────────────────────────────────

  const complianceAlerts: AlertItem[] = useMemo(() => {
    const alerts: AlertItem[] = [];

    // Beyond-repair vehicles (critical)
    for (const row of replacementDisplayRows) {
      if (row.beyondRepair) {
        alerts.push({
          id: `beyond-${row.unit}`,
          title: `${row.unit} — Beyond Repair`,
          subtitle: `${row.yearsInService} yrs in service (max ${row.maxAge} yrs)`,
          severity: "critical",
          meta: row.vehicleType || undefined,
        });
      }
    }

    // Out-of-service vehicles (warning)
    for (const row of fleetRows) {
      const unit = asString(row.unit ?? row.unit_number);
      const status = asString(row.status);
      if (isOutOfService(status)) {
        const vehicleType = asString(row.vehicle_type ?? row.apparatus_type);
        alerts.push({
          id: `oos-${unit}`,
          title: `${unit} — Out of Service`,
          subtitle: vehicleType || undefined,
          severity: "warning",
          meta: "Out of Service",
        });
      }
    }

    return alerts;
  }, [fleetRows, replacementDisplayRows]);

  // ── IRONS cross-reference ───────────────────────────────────────────────────

  const ironsBarData = useMemo(() => groupIronsByUnit(ironRows), [ironRows]);

  // ── Render ──────────────────────────────────────────────────────────────────

  const fleetLastUpdated = fleetResult.lastUpdatedAt ?? null;
  const secAgo = fleetLastUpdated
    ? Math.round((Date.now() - fleetLastUpdated.getTime()) / 1000)
    : null;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 overflow-x-hidden">
      {/* ── Page header ────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">
            Fleet &amp; Apparatus
          </h1>
          <p className="text-theme-secondary mt-1 text-sm">
            Apparatus status, age tracking, and replacement planning across all
            stations.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge label="Live" variant="green" />
          {secAgo !== null && (
            <div className="flex items-center gap-1.5 text-xs text-theme-muted">
              <span className="w-1.5 h-1.5 rounded-full bg-status-green shrink-0" />
              <span>Updated {secAgo < 5 ? "just now" : `${secAgo}s ago`}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Row-completeness notice ────────────────────────────────────
          Fleet and replacement are small tables, but this page also reads
          IRONS (24,896 rows on 2026-08-27 against a 5,000 cap) for its
          out-of-service and maintenance counts — those are computed over a
          partial read and must say so. */}
      <TruncationNoticeGroup
        sources={[
          { label: "Fleet", response: fleetResult.data },
          { label: "Fleet replacement", response: replacementResult.data },
          { label: "IRONS issues", response: ironsResult.data },
        ]}
      />

      {/* ── Date Range Toggle ──────────────────────────────────────────── */}
      <DateRangeToggle value={range} onChange={setRange} />

      {/* ── KPI Row ────────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-4">
          Fleet Overview
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            label="In Service"
            value={isLoading ? "—" : kpis.inService}
            subtitle={`of ${kpis.total} total apparatus`}
            accent="green"
            isLoading={isLoading}
            trend={
              !isLoading && kpis.total > 0
                ? {
                    direction: kpis.outOfService === 0 ? "up" : "down",
                    label:
                      kpis.outOfService === 0
                        ? "Full fleet ready"
                        : `${kpis.outOfService} offline`,
                    positiveIsGood: true,
                  }
                : undefined
            }
          />
          <KPICard
            label="Out of Service"
            value={isLoading ? "—" : kpis.outOfService}
            subtitle="apparatus currently offline"
            accent={kpis.outOfService > 0 ? "red" : "green"}
            isLoading={isLoading}
            trend={
              !isLoading
                ? {
                    direction: kpis.outOfService === 0 ? "down" : "up",
                    label:
                      kpis.outOfService === 0
                        ? "All online"
                        : "Needs attention",
                    positiveIsGood: false,
                  }
                : undefined
            }
          />
          <KPICard
            label="Total Fleet"
            value={isLoading ? "—" : kpis.total}
            subtitle="apparatus units tracked"
            accent="neutral"
            isLoading={isLoading}
          />
          <KPICard
            label="Avg Fleet Age"
            value={isLoading ? "—" : `${kpis.avgAge} yr`}
            subtitle={`target: <${DEFAULT_MAX_AGE} yr max life`}
            accent={
              kpis.avgAge > DEFAULT_MAX_AGE * 0.8
                ? "amber"
                : kpis.avgAge > 0
                  ? "green"
                  : "neutral"
            }
            isLoading={isLoading}
            trend={
              !isLoading && kpis.avgAge > 0
                ? {
                    direction:
                      kpis.avgAge > DEFAULT_MAX_AGE * 0.8 ? "up" : "down",
                    label:
                      kpis.avgAge > DEFAULT_MAX_AGE * 0.8
                        ? "Aging fleet"
                        : "Within life",
                    positiveIsGood: false,
                  }
                : undefined
            }
          />
        </div>
      </section>

      {/* ── Section 1: Fleet Overview ──────────────────────────────────── */}
      <section>
        <h2 className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-4">
          Apparatus Roster
        </h2>
        <FleetTable
          vehicles={fleetTableRows}
          isLoading={fleetResult.isLoading}
        />
      </section>

      {/* ── Section 2: Replacement Schedule ───────────────────────────── */}
      <section>
        <ReplacementTable
          rows={replacementDisplayRows}
          isLoading={replacementResult.isLoading}
        />
      </section>

      {/* ── Bottom two-column grid: alerts + IRONS ─────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Section 3: Compliance Alerts */}
        <AlertList
          title="Compliance Alerts"
          items={complianceAlerts}
          emptyMessage="No compliance alerts — all apparatus in good standing"
          isLoading={fleetResult.isLoading || replacementResult.isLoading}
          maxVisible={8}
        />

        {/* Section 4: IRONS Cross-Reference */}
        <ChartCard
          title="IRONS Issues by Apparatus"
          subtitle="Identifies problem vehicles for prioritized replacement"
          minHeight={280}
        >
          {ironsResult.isLoading ? (
            <div className="space-y-2 py-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="skeleton h-8 rounded-lg" />
              ))}
            </div>
          ) : ironsBarData.length === 0 ? (
            <div className="flex items-center justify-center h-40">
              <p className="text-sm text-theme-muted">
                No IRONS data available
              </p>
            </div>
          ) : (
            <ResponsiveContainer
              width="100%"
              height={Math.max(240, ironsBarData.length * 36)}
            >
              <BarChart
                data={ironsBarData}
                layout="vertical"
                margin={{ top: 4, right: 40, bottom: 4, left: 8 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  horizontal={false}
                  stroke={chart.gridStroke}
                />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: chart.tickColor }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <YAxis
                  type="category"
                  dataKey="unit"
                  width={80}
                  tick={{ fontSize: 11, fill: chart.tickColor }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  cursor={{ fill: chart.cursorFill }}
                  contentStyle={{
                    backgroundColor: chart.tooltipBg,
                    border: `1px solid ${chart.tooltipBorder}`,
                    color: chart.tooltipText,
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(value) => [value, "Issues"]}
                />
                <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={28}>
                  {ironsBarData.map((entry) => (
                    <Cell key={entry.unit} fill={entry.color} />
                  ))}
                  <LabelList
                    dataKey="count"
                    position="right"
                    style={{
                      fontSize: 11,
                      fill: chart.labelColor,
                      fontWeight: 600,
                    }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </div>
  );
}
